// src/components/Hub.js
import React, { useState, useEffect } from 'react';

function Hub({ setGameState, playerId, player, setPlayer, setShowHub, setGameLog }) {
  const [showBaseUpgrades, setShowBaseUpgrades] = useState(true);
  const [showAdvancedUpgrades, setShowAdvancedUpgrades] = useState(false);
  const [showEliteUpgrades, setShowEliteUpgrades] = useState(false);
  const [showTempBoosts, setShowTempBoosts] = useState(false);

  const getToken = () => localStorage.getItem('token');

  const authFetch = async (url, options = {}) => {
    const token = getToken();
    if (!token) {
      throw new Error('No authentication token found. Please log in.');
    }
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    };
    const response = await fetch(url, { ...options, headers });
    if (response.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('userId');
      localStorage.removeItem('characterId');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('currentUsername');
      setGameState('start');
      throw new Error('Session expired. Please log in again.');
    }
    return response;
  };

  const savePlayerStats = async () => {
    try {
      const payload = {
        character_id: playerId,
        player_id: player.playerId,
        hp: Math.max(0, player.hp),
        maxHp: Math.max(1, player.maxHp),
        atk: Math.max(0, player.atk),
        def: Math.max(0, player.def),
        gold: Math.max(0, player.gold),
        ectoplasm: Math.max(0, player.ectoplasm),
        level: Math.max(1, player.level),
        floorsCleared: Math.max(0, player.floorsCleared),
        kills: Math.max(0, player.kills),
        xp: Math.max(0, player.xp),
        inventory: player.inventory,
        equipped: player.equipped,
        activeQuests: player.activeQuests,
        crit_chance: player.crit_chance,
        crit_damage: player.crit_damage,
        mana: player.mana,
        max_mana: player.max_mana,
        dodge_chance: player.dodge_chance,
        status_resist: player.status_resist,
        luck: player.luck,
        tempBoosts: player.tempBoosts,
        npcInteractions: player.npcInteractions,
      };
      const response = await authFetch('https://cryptwalkergame-worker.patrick-vida.workers.dev/api/players', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        throw new Error('Failed to save player stats');
      }
    } catch (error) {
      console.error('Error saving player stats:', error.message);
      setGameLog((prev) => [...prev, `Error saving player stats: ${error.message}`]);
    }
  };

  const getEffectiveIncrement = (type, increment) => {
    const currentValue = player[type] || 0;
    let effectiveIncrement = increment;

    if (['maxHp', 'atk', 'def', 'max_mana', 'luck'].includes(type)) {
      if (currentValue <= 50) effectiveIncrement = increment;
      else if (currentValue <= 100) effectiveIncrement *= 0.9;
      else if (currentValue <= 150) effectiveIncrement *= 0.8;
      else if (currentValue <= 200) effectiveIncrement *= 0.7;
      else effectiveIncrement *= 0.5;
    } else if (['crit_chance', 'dodge_chance', 'status_resist'].includes(type)) {
      if (currentValue <= 0.5) effectiveIncrement = increment;
      else if (currentValue <= 0.75) effectiveIncrement *= 0.9;
      else if (currentValue <= 1.0) effectiveIncrement *= 0.8;
      else if (currentValue <= 1.5) effectiveIncrement *= 0.7;
      else effectiveIncrement *= 0.5;
    } else if (type === 'crit_damage') {
      if (currentValue <= 1.0) effectiveIncrement = increment;
      else if (currentValue <= 1.5) effectiveIncrement *= 0.9;
      else if (currentValue <= 2.0) effectiveIncrement *= 0.8;
      else effectiveIncrement *= 0.7;
    }

    return Math.max(0.1, effectiveIncrement);
  };

  const buyUpgrade = async (type, cost, increment) => {
    if (player.gold >= cost) {
      const effectiveIncrement = getEffectiveIncrement(type, increment);
      setPlayer((prev) => {
        const newValue = (prev[type] || 0) + effectiveIncrement;
        const updatedPlayer = {
          ...prev,
          gold: prev.gold - cost,
          [type]: newValue,
        };
        if (type === 'max_mana') {
          updatedPlayer.mana = Math.min(prev.mana, newValue);
        }
        if (type === 'maxHp') {
          updatedPlayer.hp = Math.min(prev.hp, newValue);
        }
        setGameLog((prev) => [
          ...prev,
          `Upgraded ${type.toUpperCase()} by ${effectiveIncrement.toFixed(2)}! (Cost: ${cost} Gold)`,
        ]);
        return updatedPlayer;
      });
      await savePlayerStats();
    } else {
      setGameLog((prev) => [...prev, 'Not enough gold']);
    }
  };

  const craftBuff = (type, cost) => {
    if (player.ectoplasm >= cost) {
      setPlayer((prev) => {
        const newBoosts = { ...prev.tempBoosts || {} };
        let boostEffect = 0;

        switch (type) {
          case 'hpNextRun':
            boostEffect = 25;
            newBoosts.hpNextRun = (newBoosts.hpNextRun || 0) + boostEffect;
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} HP for next run!`]);
            break;
          case 'atk5Floors':
            boostEffect = 10;
            newBoosts.atk5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} ATK for 5 floors!`]);
            break;
          case 'critChance5Floors':
            boostEffect = 0.10;
            newBoosts.critChance5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${(boostEffect * 100).toFixed(0)}% Crit Chance for 5 floors!`]);
            break;
          case 'dodgeChance5Floors':
            boostEffect = 0.10;
            newBoosts.dodgeChance5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${(boostEffect * 100).toFixed(0)}% Dodge Chance for 5 floors!`]);
            break;
          case 'def5Floors':
            boostEffect = 5;
            newBoosts.def5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} DEF for 5 floors!`]);
            break;
          case 'luck5Floors':
            boostEffect = 5;
            newBoosts.luck5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${boostEffect} Luck for 5 floors!`]);
            break;
          case 'statusResist5Floors':
            boostEffect = 0.10;
            newBoosts.statusResist5Floors = { value: boostEffect, turnsLeft: 5 };
            setGameLog((prev) => [...prev, `Crafted +${(boostEffect * 100).toFixed(0)}% Status Resist for 5 floors!`]);
            break;
          default:
            return prev;
        }

        const updatedPlayer = {
          ...prev,
          ectoplasm: prev.ectoplasm - cost,
          tempBoosts: newBoosts,
        };
        savePlayerStats();
        return updatedPlayer;
      });
    } else {
      setGameLog((prev) => [...prev, 'Not enough ectoplasm']);
    }
  };

  if (!player) {
    return <div>Loading Hub...</div>;
  }

  const activeBoosts = [];
  if (player.tempBoosts?.hpNextRun) {
    activeBoosts.push(`+${player.tempBoosts.hpNextRun} HP (next run)`);
  }
  if (player.tempBoosts?.atk5Floors) {
    activeBoosts.push(`+${player.tempBoosts.atk5Floors.value} ATK (${player.tempBoosts.atk5Floors.turnsLeft} floors)`);
  }
  if (player.tempBoosts?.critChance5Floors) {
    activeBoosts.push(`+${(player.tempBoosts.critChance5Floors.value * 100).toFixed(0)}% Crit Chance (${player.tempBoosts.critChance5Floors.turnsLeft} floors)`);
  }
  if (player.tempBoosts?.dodgeChance5Floors) {
    activeBoosts.push(`+${(player.tempBoosts.dodgeChance5Floors.value * 100).toFixed(0)}% Dodge Chance (${player.tempBoosts.dodgeChance5Floors.turnsLeft} floors)`);
  }
  if (player.tempBoosts?.def5Floors) {
    activeBoosts.push(`+${player.tempBoosts.def5Floors.value} DEF (${player.tempBoosts.def5Floors.turnsLeft} floors)`);
  }
  if (player.tempBoosts?.luck5Floors) {
    activeBoosts.push(`+${player.tempBoosts.luck5Floors.value} Luck (${player.tempBoosts.luck5Floors.turnsLeft} floors)`);
  }
  if (player.tempBoosts?.statusResist5Floors) {
    activeBoosts.push(`+${(player.tempBoosts.statusResist5Floors.value * 100).toFixed(0)}% Status Resist (${player.tempBoosts.statusResist5Floors.turnsLeft} floors)`);
  }

  return (
    <div className="hub">
      <div className="hub-header">
        <h2>Hub</h2>
        {/* Close button is now styled directly in .hub .close-button */}
        <button className="close-button" onClick={() => setShowHub(false)}>
          ✕
        </button>
      </div>
      <div className="hub-text">
        <p>Gold: {player.gold} | Ectoplasm: {player.ectoplasm}</p>
        {activeBoosts.length > 0 && (
          <p>Active Boosts: {activeBoosts.join(', ')}</p>
        )}
      </div>

      <div className="upgrade-category">
        <button className="category-toggle" onClick={() => setShowBaseUpgrades(!showBaseUpgrades)}>
          Base Upgrades {showBaseUpgrades ? '▲' : '▼'}
        </button>
        {showBaseUpgrades && (
          <div className="upgrade-list">
            <div className="upgrade-item">
              <p>Upgrade HP</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('maxHp', 5, 1)}
              >
                +1 HP (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade ATK</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('atk', 5, 1)}
              >
                +1 ATK (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade DEF</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('def', 5, 1)}
              >
                +1 DEF (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Mana</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('max_mana', 5, 1)}
              >
                +1 Mana (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Crit Chance</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('crit_chance', 5, 0.18)}
              >
                +18% Crit Chance (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Crit Damage</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('crit_damage', 5, 0.05)}
              >
                +5% Crit Damage (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Dodge Chance</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('dodge_chance', 5, 0.18)}
              >
                +18% Dodge Chance (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Status Resist</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('status_resist', 5, 0.18)}
              >
                +18% Status Resist (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Upgrade Luck</p>
              <button
                className={`hub-button hub-upgrade ${player.gold >= 5 ? 'affordable' : ''}`}
                onClick={() => buyUpgrade('luck', 5, 1)}
              >
                +1 Luck (5 Gold)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft HP Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('hpNextRun', 5)}
              >
                Craft HP Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft ATK Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('atk5Floors', 5)}
              >
                Craft ATK Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft Crit Chance Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('critChance5Floors', 5)}
              >
                Craft Crit Chance Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft Dodge Chance Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('dodgeChance5Floors', 5)}
              >
                Craft Dodge Chance Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft DEF Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('def5Floors', 5)}
              >
                Craft DEF Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft Luck Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('luck5Floors', 5)}
              >
                Craft Luck Buff (5 Ectoplasm)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Craft Status Resist Buff</p>
              <button
                className={`hub-button hub-craft ${player.ectoplasm >= 5 ? 'affordable' : ''}`}
                onClick={() => craftBuff('statusResist5Floors', 5)}
              >
                Craft Status Resist Buff (5 Ectoplasm)
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Upgrades (Unlock at Level 5) */}
      {player.level >= 5 && (
        <div className="upgrade-category">
          <button className="category-toggle" onClick={() => setShowAdvancedUpgrades(!showAdvancedUpgrades)}>
            Advanced Upgrades {showAdvancedUpgrades ? '▲' : '▼'}
          </button>
          {showAdvancedUpgrades && (
            <div className="upgrade-list">
              <div className="upgrade-item">
                <p>Advanced HP Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('maxHp', 20, 5)}
                >
                  +5 HP (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced ATK Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('atk', 20, 5)}
                >
                  +5 ATK (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced DEF Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('def', 20, 5)}
                >
                  +5 DEF (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Mana Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('max_mana', 20, 5)}
                >
                  +5 Mana (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Crit Chance Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('crit_chance', 20, 0.5)}
                >
                  +50% Crit Chance (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Crit Damage Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('crit_damage', 20, 0.2)}
                >
                  +20% Crit Damage (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Dodge Chance Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('dodge_chance', 20, 0.5)}
                >
                  +50% Dodge Chance (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Status Resist Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('status_resist', 20, 0.5)}
                >
                  +50% Status Resist (20 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Advanced Luck Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 20 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('luck', 20, 5)}
                >
                  +5 Luck (20 Gold)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Elite Upgrades (Unlock at Level 10) */}
      {player.level >= 10 && (
        <div className="upgrade-category">
          <button className="category-toggle" onClick={() => setShowEliteUpgrades(!showEliteUpgrades)}>
            Elite Upgrades {showEliteUpgrades ? '▲' : '▼'}
          </button>
          {showEliteUpgrades && (
            <div className="upgrade-list">
              <div className="upgrade-item">
                <p>Elite HP Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('maxHp', 50, 10)}
                >
                  +10 HP (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite ATK Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('atk', 50, 10)}
                >
                  +10 ATK (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite DEF Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('def', 50, 10)}
                >
                  +10 DEF (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Mana Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('max_mana', 50, 10)}
                >
                  +10 Mana (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Crit Chance Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('crit_chance', 50, 1.0)}
                >
                  +100% Crit Chance (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Crit Damage Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('crit_damage', 50, 0.5)}
                >
                  +50% Crit Damage (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Dodge Chance Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('dodge_chance', 50, 1.0)}
                >
                  +100% Dodge Chance (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Status Resist Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('status_resist', 50, 1.0)}
                >
                  +100% Status Resist (50 Gold)
                </button>
              </div>
              <div className="upgrade-item">
                <p>Elite Luck Upgrade</p>
                <button
                  className={`hub-button hub-upgrade ${player.gold >= 50 ? 'affordable' : ''}`}
                  onClick={() => buyUpgrade('luck', 50, 10)}
                >
                  +10 Luck (50 Gold)
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="upgrade-category">
        <button className="category-toggle" onClick={() => setShowTempBoosts(!showTempBoosts)}>
          MTX {showTempBoosts ? '▲' : '▼'}
        </button>
        {showTempBoosts && (
          <div className="upgrade-list">
            <div className="upgrade-item">
              <p>Unlock new zones and challenges</p>
              <button className="hub-button hub-mtx">
                Unlock Ethereal Abyss ($1.99)
              </button>
            </div>
            <div className="upgrade-item">
              <p>Future MTX content</p>
              <button className="hub-button hub-mtx" disabled>
                Coming Soon
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="packs-placeholder">
        <p>(Packs coming soon: Combat, Survival, Balanced)</p>
      </div>
    </div>
  );
}

export default Hub;